import { useState } from "react";
import "@/App.css";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Wifi,
  Shield,
  DollarSign,
  Clock,
  CheckCircle,
  Building2,
  Hotel,
  UtensilsCrossed,
  Users,
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  Menu,
  X,
  Zap,
  TrendingUp,
  HeadphonesIcon,
  RefreshCw } from
"lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"@/components/ui/select";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_connect-upgrade-1/artifacts/kvr0k4ib_SEAM_Logo.png";

// Animation variants
const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const stagger = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true }
};

// Navigation Component
const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
  { href: "#problem", label: "The Problem" },
  { href: "#solution", label: "Solution" },
  { href: "#benefits", label: "Benefits" },
  { href: "#how-it-works", label: "How It Works" },
  { href: "#contact", label: "Contact" }];


  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed top-0 left-0 right-0 z-50 glass">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#" className="flex items-center gap-3" data-testid="nav-logo">
            <img
              src={LOGO_URL}
              alt="SEAM Logo"
              className="h-12 w-auto" />

          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) =>
            <a
              key={link.href}
              href={link.href}
              className="nav-link text-sm font-medium"
              data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}>

                {link.label}
              </a>
            )}
          </div>

          {/* CTA Button */}
          <div className="hidden md:block">
            <a href="#contact">
              <Button
                className="btn-primary glow-primary px-6 py-2 rounded-full font-medium"
                data-testid="nav-cta-button">

                Request Assessment
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button">

            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen &&
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="md:hidden pb-6">

            <div className="flex flex-col gap-4">
              {navLinks.map((link) =>
            <a
              key={link.href}
              href={link.href}
              className="nav-link text-base font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
              data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(' ', '-')}`}>

                  {link.label}
                </a>
            )}
              <a href="#contact" onClick={() => setMobileMenuOpen(false)}>
                <Button
                className="btn-primary glow-primary w-full mt-4 rounded-full"
                data-testid="mobile-nav-cta-button">

                  Request Assessment
                </Button>
              </a>
            </div>
          </motion.div>
        }
      </div>
    </motion.nav>);

};

// Hero Section
const HeroSection = () => {
  return (
    <section className="hero-bg flex items-center justify-center !py-[80px]" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fadeUp}>
            <span className="inline-block px-4 py-2 rounded-full bg-seam-blue/10 border border-seam-blue/20 text-seam-light text-sm font-medium mb-6">
              Zero Cost • Zero Hassle • Zero Risk
            </span>
            <h1 className="font-heading sm:text-5xl lg:text-6xl xl:text-7xl !font-bold !text-4xl mb-6 text-white">
              Premium Wi-Fi  UpgradeAt No Cost<br />
              <span className="text-gradient">Cellular Upgrade</span><br />
              At No Cost
            </h1>
            <p className="text-lg text-slate-300 mb-8 max-w-xl leading-relaxed">
              We deploy carrier-grade Wi-Fi networks inside your property that improve both Wi-Fi performance and cellular experience — completely free. You benefit. We handle everything.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="#contact">
                <Button
                  size="lg"
                  className="btn-primary glow-primary rounded-full px-8 py-6 text-lg font-semibold"
                  data-testid="hero-cta-primary">

                  Get Free Assessment
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <a href="#how-it-works">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8 py-6 text-lg border-white/20 text-white hover:bg-white/5"
                  data-testid="hero-cta-secondary">

                  See How It Works
                </Button>
              </a>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden lg:flex justify-center">

            <div className="relative">
              <div className="absolute inset-0 bg-seam-blue/20 blur-3xl rounded-full"></div>
              <img
                src={LOGO_URL}
                alt="SEAM"
                className="relative w-80 h-80 object-contain logo-float"
                data-testid="hero-logo" />

            </div>
          </motion.div>
        </div>
      </div>
    </section>);

};

// Problem Section
const ProblemSection = () => {
  const problems = [
  { icon: Wifi, text: "Poor indoor cellular service leading to dropped calls" },
  { icon: TrendingUp, text: "Overloaded Wi-Fi causing slow speeds and complaints" },
  { icon: Users, text: "Frustrated guests leaving negative reviews" },
  { icon: DollarSign, text: "Traditional upgrades are expensive and disruptive" }];


  return (
    <section id="problem" className="md:py-32 sm:px-6 lg:px-8 section-divider !py-[80px] !px-[16px]" data-testid="problem-section">
      <div className="max-w-7xl mx-auto">
        <motion.div {...fadeUp} className="text-center mb-16">
          <span className="text-seam-light text-sm font-semibold tracking-wider uppercase mb-4 block">
            The Problem
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold text-white mb-6">
            Connectivity Expectations Have Changed
          </h2>
          <p className="text-slate-400 text-lg max-w-3xl mx-auto">
            Guests, tenants, and patrons expect fast, reliable connectivity everywhere. Poor service leads to complaints, low reviews, and lost revenue.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {problems.map((problem, index) =>
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="benefit-card rounded-2xl p-6 flex items-start gap-4 card-hover"
            data-testid={`problem-card-${index}`}>

              <div className="icon-container p-3 rounded-xl flex-shrink-0">
                <problem.icon className="w-6 h-6 text-seam-light" />
              </div>
              <p className="text-slate-300 text-lg">{problem.text}</p>
            </motion.div>
          )}
        </div>
      </div>
    </section>);

};

// Solution Section
const SolutionSection = () => {
  return (
    <section id="solution" className="md:py-32 sm:px-6 lg:px-8 !py-[80px] !px-[16px] bg-[#0a0f1a]" data-testid="solution-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div {...fadeUp}>
            <span className="text-seam-light text-sm font-semibold tracking-wider uppercase mb-4 block">
              The Solution
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold text-white mb-6">
              Enterprise Connectivity,<br />Zero Investment
            </h2>
            <p className="text-slate-300 text-lg mb-8 leading-relaxed">
              We deploy and operate a carrier-grade Wi-Fi network inside your property that improves both Wi-Fi performance and cellular experience — at no cost to you.
            </p>
            <p className="text-slate-400 text-base mb-8">
              Our network securely offloads mobile traffic from wireless carriers onto high-performance Wi-Fi, relieving cellular congestion and delivering a smoother experience for your guests and staff.
            </p>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <CheckCircle className="text-green-500 w-5 h-5" />
                <span className="text-slate-300">No Contracts</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="text-green-500 w-5 h-5" />
                <span className="text-slate-300">No IT Burden</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="text-green-500 w-5 h-5" />
                <span className="text-slate-300">No Invoices</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative">

            <div className="glass rounded-3xl p-8 border border-white/10">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-seam-blue/20 border border-seam-blue/30 mb-4">
                  <Wifi className="w-10 h-10 text-seam-light" />
                </div>
                <h3 className="font-heading text-2xl font-semibold text-white">You Get Modern Connectivity</h3>
                <p className="text-slate-400 mt-2">We Handle Everything</p>
              </div>
              <div className="space-y-4">
                {["Design & Installation", "24×7 Monitoring", "Ongoing Maintenance", "Technology Upgrades"].map((item, i) =>
                <div key={i} className="flex items-center gap-3 text-slate-300">
                    <div className="w-2 h-2 rounded-full bg-seam-light"></div>
                    <span>{item}</span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>);

};

// Benefits Section
const BenefitsSection = () => {
  const benefits = [
  {
    icon: DollarSign,
    title: "Zero Upfront Costs",
    description: "No capital investment or equipment costs required from you"
  },
  {
    icon: Wifi,
    title: "Enterprise-Grade Wi-Fi",
    description: "High-density network designed for demanding environments"
  },
  {
    icon: Phone,
    title: "Better Cellular",
    description: "Improved indoor cellular performance for all carriers"
  },
  {
    icon: HeadphonesIcon,
    title: "24×7 Support",
    description: "Round-the-clock monitoring, maintenance, and support"
  },
  {
    icon: RefreshCw,
    title: "Ongoing Upgrades",
    description: "Technology evolves automatically as standards improve"
  },
  {
    icon: Shield,
    title: "No Carrier Contracts",
    description: "We handle all carrier relationships and negotiations"
  }];


  return (
    <section id="benefits" className="md:py-32 sm:px-6 lg:px-8 section-divider !py-[80px] !px-[16px]" data-testid="benefits-section">
      <div className="max-w-7xl mx-auto">
        <motion.div {...fadeUp} className="text-center mb-16">
          <span className="text-seam-light text-sm font-semibold tracking-wider uppercase mb-4 block">
            What You Get
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold text-white mb-6">
            Premium Benefits, Zero Investment
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Everything you need for world-class connectivity without any financial or technical burden.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, index) =>
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="benefit-card rounded-2xl p-8 card-hover"
            data-testid={`benefit-card-${index}`}>

              <div className="icon-container w-14 h-14 rounded-xl flex items-center justify-center mb-5">
                <benefit.icon className="w-7 h-7 text-seam-light" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-white mb-3">{benefit.title}</h3>
              <p className="text-slate-400 leading-relaxed">{benefit.description}</p>
            </motion.div>
          )}
        </div>
      </div>
    </section>);

};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
  {
    number: "01",
    title: "Site Assessment",
    description: "We perform a no-obligation assessment to confirm suitability and provide a deployment plan."
  },
  {
    number: "02",
    title: "Design & Install",
    description: "We design and install the Wi-Fi infrastructure at our expense with minimal disruption."
  },
  {
    number: "03",
    title: "Carrier Integration",
    description: "Wireless carriers pay us to securely offload traffic onto the network we built."
  },
  {
    number: "04",
    title: "Ongoing Operation",
    description: "Carrier fees fund deployment, operation, and support. You enjoy better connectivity."
  }];


  return (
    <section id="how-it-works" className="md:py-32 sm:px-6 lg:px-8 !py-[80px] !px-[16px] bg-[#0a0f1a]" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto">
        <motion.div {...fadeUp} className="text-center mb-16">
          <span className="text-seam-light text-sm font-semibold tracking-wider uppercase mb-4 block">
            How It Works
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold text-white mb-6">
            Simple Process, Powerful Results
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            From assessment to operation, we handle every step so you can focus on your business.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) =>
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.15 }}
            className="relative"
            data-testid={`step-card-${index}`}>

              <div className="step-number w-14 h-14 rounded-full flex items-center justify-center text-xl mb-6">
                {step.number}
              </div>
              <h3 className="font-heading text-xl font-semibold text-white mb-3">{step.title}</h3>
              <p className="text-slate-400 leading-relaxed">{step.description}</p>
              {index < steps.length - 1 &&
            <div className="hidden lg:block absolute top-7 left-20 w-full h-0.5 bg-gradient-to-r from-seam-blue/50 to-transparent"></div>
            }
            </motion.div>
          )}
        </div>
      </div>
    </section>);

};

// Why Choose Section
const WhyChooseSection = () => {
  const reasons = [
  "Zero upfront or ongoing costs",
  "Improved guest satisfaction and reviews",
  "Faster deployment than traditional cellular solutions",
  "No disruption to daily operations",
  "One accountable partner for everything wireless"];


  return (
    <section className="md:py-32 sm:px-6 lg:px-8 section-divider !py-[80px] !px-[16px]" data-testid="why-choose-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div {...fadeUp}>
            <span className="text-seam-light text-sm font-semibold tracking-wider uppercase mb-4 block">
              Why Property Owners Choose This
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold text-white mb-8">
              The Smart Choice for Modern Properties
            </h2>
            <div className="space-y-4">
              {reasons.map((reason, index) =>
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="flex items-center gap-4"
                data-testid={`reason-${index}`}>

                  <div className="w-6 h-6 rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  </div>
                  <span className="text-slate-300 text-lg">{reason}</span>
                </motion.div>
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass rounded-3xl p-8 border border-white/10">

            <h3 className="font-heading text-2xl font-semibold text-white mb-6">What We Need From You</h3>
            <div className="space-y-4 mb-8">
              {[
              "Permission to install and operate the Wi-Fi infrastructure",
              "Access to agreed-upon locations for equipment placement",
              "Standard site coordination during installation"].
              map((item, i) =>
              <div key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-seam-light mt-1 flex-shrink-0" />
                  <span className="text-slate-300">{item}</span>
                </div>
              )}
            </div>
            <div className="pt-6 border-t border-white/10">
              <p className="text-slate-400 text-sm">
                That's it. No carrier negotiations. No IT management. No invoices.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>);

};

// Who This Is For Section
const WhoThisIsForSection = () => {
  const properties = [
  { icon: Hotel, title: "Hotels", description: "Deliver exceptional guest experiences with reliable connectivity" },
  { icon: Building2, title: "Residential Buildings", description: "Keep tenants connected and satisfied" },
  { icon: UtensilsCrossed, title: "Restaurants", description: "Enable seamless dining and payment experiences" },
  { icon: Users, title: "Venues", description: "Support high-density events with enterprise Wi-Fi" }];


  return (
    <section className="md:py-32 sm:px-6 lg:px-8 !py-[80px] !px-[16px] bg-[#0a0f1a]" data-testid="who-this-is-for-section">
      <div className="max-w-7xl mx-auto">
        <motion.div {...fadeUp} className="text-center mb-16">
          <span className="text-seam-light text-sm font-semibold tracking-wider uppercase mb-4 block">
            Who This Is For
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold text-white mb-6">
            Properties Where Connectivity Matters
          </h2>
          <p className="text-slate-400 text-lg max-w-3xl mx-auto">
            For properties where reliable connectivity directly impacts experience, reputation, and revenue.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {properties.map((property, index) =>
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="property-card rounded-2xl p-8 text-center card-hover"
            data-testid={`property-card-${index}`}>

              <div className="icon-container w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <property.icon className="w-8 h-8 text-seam-light" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-white mb-2">{property.title}</h3>
              <p className="text-slate-400 text-sm">{property.description}</p>
            </motion.div>
          )}
        </div>
      </div>
    </section>);

};

// Assessment Form Section
const AssessmentForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    propertyType: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/assessment`, formData);
      toast.success("Assessment request submitted! We'll be in touch shortly.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        propertyType: "",
        message: ""
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="md:py-32 sm:px-6 lg:px-8 section-divider !py-[80px] !px-[16px]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16">
          <motion.div {...fadeUp}>
            <span className="text-seam-light text-sm font-semibold tracking-wider uppercase mb-4 block">
              Get Started
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold text-white mb-6">
              Request Your Free<br />Site Assessment
            </h2>
            <p className="text-slate-400 text-lg mb-8">
              We perform a no-obligation site assessment to confirm suitability and provide a deployment plan. If approved, we handle the rest.
            </p>
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="icon-container w-12 h-12 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-seam-light" />
                </div>
                <div>
                  <h4 className="text-white font-medium">No-Obligation Assessment</h4>
                  <p className="text-slate-400 text-sm">Free site evaluation with no commitment required</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="icon-container w-12 h-12 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-seam-light" />
                </div>
                <div>
                  <h4 className="text-white font-medium">Fast Response</h4>
                  <p className="text-slate-400 text-sm">We'll contact you within 24-48 hours</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="icon-container w-12 h-12 rounded-xl flex items-center justify-center">
                  <Zap className="w-6 h-6 text-seam-light" />
                </div>
                <div>
                  <h4 className="text-white font-medium">Quick Deployment</h4>
                  <p className="text-slate-400 text-sm">Faster than traditional cellular solutions</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}>

            <form onSubmit={handleSubmit} className="glass rounded-3xl p-8 border border-white/10" data-testid="assessment-form">
              <div className="grid sm:grid-cols-2 gap-6 mb-6">
                <div>
                  <Label htmlFor="name" className="text-slate-300 mb-2 block">Full Name *</Label>
                  <Input
                    id="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="form-input bg-white/5 border-white/10 text-white placeholder:text-slate-500"
                    placeholder="John Smith"
                    data-testid="input-name" />

                </div>
                <div>
                  <Label htmlFor="email" className="text-slate-300 mb-2 block">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="form-input bg-white/5 border-white/10 text-white placeholder:text-slate-500"
                    placeholder="john@company.com"
                    data-testid="input-email" />

                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6 mb-6">
                <div>
                  <Label htmlFor="phone" className="text-slate-300 mb-2 block">Phone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="form-input bg-white/5 border-white/10 text-white placeholder:text-slate-500"
                    placeholder="+1 (555) 000-0000"
                    data-testid="input-phone" />

                </div>
                <div>
                  <Label htmlFor="company" className="text-slate-300 mb-2 block">Company/Property Name</Label>
                  <Input
                    id="company"
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="form-input bg-white/5 border-white/10 text-white placeholder:text-slate-500"
                    placeholder="Company Name"
                    data-testid="input-company" />

                </div>
              </div>

              <div className="mb-6">
                <Label htmlFor="propertyType" className="text-slate-300 mb-2 block">Property Type</Label>
                <Select
                  value={formData.propertyType}
                  onValueChange={(value) => setFormData({ ...formData, propertyType: value })}>

                  <SelectTrigger
                    className="form-input bg-white/5 border-white/10 text-white"
                    data-testid="select-property-type">

                    <SelectValue placeholder="Select property type" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-white/10">
                    <SelectItem value="hotel">Hotel</SelectItem>
                    <SelectItem value="residential">Residential Building</SelectItem>
                    <SelectItem value="restaurant">Restaurant</SelectItem>
                    <SelectItem value="venue">Venue / Event Space</SelectItem>
                    <SelectItem value="office">Office Building</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-8">
                <Label htmlFor="message" className="text-slate-300 mb-2 block">Additional Details</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="form-input bg-white/5 border-white/10 text-white placeholder:text-slate-500 min-h-[120px]"
                  placeholder="Tell us about your property and connectivity challenges..."
                  data-testid="input-message" />

              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary glow-primary w-full rounded-full py-6 text-lg font-semibold"
                data-testid="submit-button">

                {isSubmitting ? "Submitting..." : "Request Free Assessment"}
                {!isSubmitting && <ArrowRight className="ml-2 h-5 w-5" />}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>);

};

// Footer
const Footer = () => {
  return (
    <footer className="py-16 px-4 sm:px-6 lg:px-8 bg-[#020617] border-t border-white/5" data-testid="footer">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          <div>
            <img src={LOGO_URL} alt="SEAM Logo" className="h-16 w-auto mb-6" />
            <p className="text-slate-400 text-sm leading-relaxed">
              Southern Entertainment & Marketing delivers enterprise-grade Wi-Fi and cellular solutions at zero cost to property owners.
            </p>
          </div>
          
          <div>
            <h4 className="font-heading text-lg font-semibold text-white mb-4">Quick Links</h4>
            <div className="space-y-3">
              {["The Problem", "Solution", "Benefits", "How It Works", "Contact"].map((link) =>
              <a
                key={link}
                href={`#${link.toLowerCase().replace(' ', '-')}`}
                className="block text-slate-400 hover:text-white text-sm transition-colors duration-200"
                data-testid={`footer-link-${link.toLowerCase().replace(' ', '-')}`}>

                  {link}
                </a>
              )}
            </div>
          </div>
          
          <div>
            <h4 className="font-heading text-lg font-semibold text-white mb-4">Contact</h4>
            <div className="space-y-4">
              <a href="mailto:info@southernem.com" className="flex items-center gap-3 text-slate-400 hover:text-white text-sm transition-colors duration-200" data-testid="footer-email">
                <Mail className="w-4 h-4" />
                info@southernem.com
              </a>
              <a href="tel:+17866721488" className="flex items-center hover:text-white transition-colors duration-200 !text-sm gap-3 text-slate-400" data-testid="footer-phone">
                <Phone className="w-4 h-4" />
                +1 (786) 672-1488
              </a>
              <div className="flex items-start gap-3 text-slate-400 text-sm" data-testid="footer-address">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  2399 Salzedo Street<br />
                  Suite 207<br />
                  Coral Gables, FL 33134
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="pt-8 border-t border-white/5 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} Southern Entertainment & Marketing. All rights reserved.
          </p>
          <p className="text-slate-500 text-sm">
            Better connectivity. Zero cost. No hassle.
          </p>
        </div>
      </div>
    </footer>);

};

// Main App
function App() {
  return (
    <div className="min-h-screen bg-[#020617]">
      <Toaster position="top-right" theme="dark" />
      <Navigation />
      <HeroSection />
      <ProblemSection />
      <SolutionSection />
      <BenefitsSection />
      <HowItWorksSection />
      <WhyChooseSection />
      <WhoThisIsForSection />
      <AssessmentForm />
      <Footer />
    </div>);

}

export default App;